import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UserProfileForm } from '@traquility/ui/diary';
import type {
  UserProfileFormData,
  UserProfileFormContainerProps,
} from '../shared/types/userProfileForm';

export type { UserProfileFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const UPDATE_PROFILE_MUTATION = `
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      user {
        id
        name
        email
      }
      errors
    }
  }
`;

function UserProfileFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  languageOptions,
  timeZoneOptions,
  errors: initialErrors = {},
}: UserProfileFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (data: UserProfileFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const input = {
      name: data.name,
      email: data.email,
      language: data.language,
      timeZone: data.timeZone,
      emailOnReports: data.emailOnReports,
      emailOnAchievements: data.emailOnAchievements,
      pushOnReports: data.pushOnReports,
      pushOnAchievements: data.pushOnAchievements,
      reminderEnabled: data.reminderEnabled,
      reminderTime: data.reminderTime,
    };

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: UPDATE_PROFILE_MUTATION, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      if (result.data.updateProfile.errors?.length > 0) {
        setErrors({ base: result.data.updateProfile.errors });
        setIsSubmitting(false);
        return;
      }

      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  return (
    <UserProfileForm
      initialData={initialData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      errors={errors}
      labels={labels}
      languageOptions={languageOptions}
      timeZoneOptions={timeZoneOptions}
      isSubmitting={isSubmitting}
    />
  );
}

function UserProfileFormContainer(props: UserProfileFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <UserProfileFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountUserProfileForm(
  container: HTMLElement,
  props: UserProfileFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<UserProfileFormContainer {...props} />);
  return root;
}

export function unmountUserProfileForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default UserProfileFormContainer;
